import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  maxContactsAllowed,
  maxPhonesAllowed,
  maxEmailsAllowed,
  maxPortalsAllowed  
} from '../../../settings/appConfig';
import useValidation from '../../validation/useValidation';
import { DEFAULT_SERVE_PREFERENCES } from '../../../utils/formDefaults';

export default function useMainInfoForm({
  phoneTypes,
  portalTypes,
  sourceTypes,
  custodianTypes,
  recordTypes,
  states,
}: {
  phoneTypes: any[];
  portalTypes: any[];
  custodianTypes: any[];
  sourceTypes: any[];
  recordTypes: any[];
  states: any[];
}) {
  const {
    validateName,
    validateURL,
    validatePhoneNumber,
    validateEmail,
    validatePostalCode,
  } = useValidation();

  // ✅ Validation schema
  const validationSchema = Yup.object()
    .shape({
      name: validateName(true, 'Name', 120, 3),
      typeId: Yup.string().oneOf(
        custodianTypes.map((o) => o.id.toString()),
        'Invalid custodian type',
      ),
      isFacility: Yup.boolean(),
      isCustodian: Yup.boolean(),

      portalUrls: Yup.array()
        .of(
          Yup.object().shape({
            portalUrl: validateURL(),
            hasDuplicateError: Yup.boolean(),
          }),
        )
        .max(
          maxPortalsAllowed,
          `Portals can have at most ${maxPortalsAllowed} entries`,
        )
        .test('unique', 'Cannot insert duplicate portals', function (value) {
          const { createError, path } = this;
          if (value && Array.isArray(value) && value.length > 1) {
            const portals = value.map((v) => v.portalUrl);
            if (new Set(portals).size !== portals.length) {
              return createError({
                path,
                message: 'Cannot insert duplicate portals',
              });
            }
          }
          return true;
        }),

      phones: Yup.array()
        .of(
          Yup.object().shape({
            phoneNumber: validatePhoneNumber(true),
            phoneTypeId: Yup.string()
              .oneOf(
                phoneTypes.map((o) => o.id.toString()),
                'Invalid Phone type',
              )
              .required('Phone type is required'),
            isPrimary: Yup.boolean(),
            hasDuplicateError: Yup.boolean(),
          }),
        )
        .min(1, 'Phones must have at least 1 entry')
        .max(
          maxPhonesAllowed,
          `Phones can have at most ${maxPhonesAllowed} entries`,
        )
        .test('unique', 'Cannot insert duplicate phones', function (value) {
          const { createError, path } = this;
          if (value && Array.isArray(value) && value.length > 1) {
            const phones = value.map((v) => v.phoneNumber);
            if (new Set(phones).size !== phones.length) {
              return createError({
                path,
                message: 'Cannot insert duplicate phones',
              });
            }
          }
          return true;
        }),

      emails: Yup.array()
        .of(
          Yup.object().shape({
            email: validateEmail(),
            hasDuplicateError: Yup.boolean(),
          }),
        )
        .max(
          maxEmailsAllowed,
          `Email can have at most ${maxEmailsAllowed} entries`,
        )
        .test('unique', 'Cannot insert duplicate emails', function (value) {
          const { createError, path } = this;
          if (value && Array.isArray(value) && value.length > 1) {
            const emails = value.map((v) => v.email);
            if (new Set(emails).size !== emails.length) {
              return createError({
                path,
                message: 'Cannot insert duplicate emails',
              });
            }
          }
          return true;
        }),

      addressInfo: Yup.object().shape({
        addressDetails: Yup.string()
          .max(120, "Address can't exceed 120 characters")
          .required('Address is required'),
        city: Yup.string()
          .max(40, "City can't exceed 40 characters")
          .required('City is required'),
        stateId: Yup.string()
          .required('State is required')
          .oneOf(
            states.map((o) => o.id.toString()),
            'Invalid State',
          ),
        postCode: validatePostalCode(true),
      }),
      contacts: Yup.array()
        .of(
          Yup.object().shape({
            name: validateName(),
            email: validateEmail(),
            phoneNumber: validatePhoneNumber(),
            title: validateName(false, 'Title'),
            isPrimary: Yup.boolean(),
          }),
        )
        .max(
          maxContactsAllowed,
          `Contacts can have at most ${maxContactsAllowed} entries`,
        ),
    })
    .test(
      'at-least-one-selected',
      'Please select either Facility or Custodian',
      function (value) {
        if (!value?.isFacility && !value?.isCustodian) {
          return this.createError({
            path: 'entityType',
            message: 'Please select either Facility or Custodian',
          });
        }
        return true;
      },
    );

  const mainInfoForm: any = useFormik({
    initialValues: {
      name: null,
      isFacility: false,
      isCustodian: false,
      notes: null,
      disabled: false,
      facilityTypes: [],
      facilityTypeNotes: null,
      addressInfo: {
        addressDetails: null,
        city: null,
        stateId: null,
        postCode: null,
        timeZoneId: null,
      },
      mailingAddress: {
        addressDetails: '',
        city: '',
        stateId: '',
        postCode: '',
        timeZoneId: null,
      },
      emails: [{ email: '', hasDuplicateError: false }],
      portalUrls: [
        {
          portalUrl: '',
          portalTypeId: null,
          portalNotes: null,
          hasDuplicateError: false,
        },
      ],
      externalSourceMappings: [
        {
          mappedId: '',
          sourceTypeId: null,
          notes: null,
        },
      ],
      phones: [
        {
          isPrimary: false,
          phoneNumber: null,
          phoneTypeId: null,
          hasDuplicateError: false,
        },
      ],
      contacts: [
        {
          isPrimary: false,
          email: '',
          phoneNumber: '',
          name: '',
          title: '',
        },
      ],
      departmentAndPhysicians: [
        {
          department: null,
          name: null,
          phoneNumber: null,
          additionalInformation: null,
        },
      ],
      custodianDetails: {
        recordTypes: [],
        recordTypeNotes: null,
        orderTypeId: null,
        orderTypeNotes: null,
        daysAvailable: [],
        serveMethods: [],
        roiMethods: [],
        serveMethodNotes: null,
        roiMethodNotes: null,
        followUpMethods: [],
        entityMetric: {
          averageProcessingTime: null,
          lastRecordRetrievedDate: null,
        },
        servePreferences: DEFAULT_SERVE_PREFERENCES,
        billingInformation: {
          payeeName: null,
          payeeAddress: null,
          payeeContact: null,
          payeeCity: null,
          processingFeesAuthorization: null,
          processingFeesSubpoena: null,
          payeePostCode: null,
          payeeStateId: null,
          invoiceBeforeRecords: false,
          invoicePaymentMethods: [],
          processingFeesMethods: [],
          initialProcessingFee: false,
          initialProcessingFeeNotes: null,
        },
      },
    },
    validationSchema,
    onSubmit: (values) => {
      console.log('Submitted:', values);
    },
  });

  return {
    mainInfoForm,
  };
}
